import { useEffect, useState } from "react";
import { CategoryWithParts, IPart } from "../../../../models/Part";
import PriceFormat from "../../../../utils/priceFormat";
import { formatMinutesIntoHours } from "../../../../utils/formatMinutesIntoHours";
import { ButtonInput } from "../../../shared/Inputs/ButtonInput";
import { CreateJobRequest, JobType } from "../../../../models/Job";
import { ILead } from "../../../../models/Lead";
import { IVehicle } from "../../../../models/Vehicle";
import useDefaultCRUDHandlers from "../../../../hooks/useDefaultCRUDHandlers";
import useJobMutations from "../../../../data/useJobMutations";
import usePartMutations from "../../../../data/usePartMutations";
import { DateTime } from "luxon";


const SummaryCard = ({ title, time, price }: { title: string, time: number, price: number }) => (
  <div className="bg-white shadow rounded-lg p-4 w-full">
    <h1 className="text-lg font-semibold">{title}</h1>
    <div className="flex justify-between">
      <div>
        <p className="text-gray-600 text-sm">Est. Time</p>
        <p>{formatMinutesIntoHours(time)}</p>
      </div>
      <div>
        <p className="text-gray-600 text-sm">Price</p>
        <p>{PriceFormat(price)}</p>
      </div>
    </div>
  </div>
);

interface SelectedCategoriesProps {
  selectedCategories: CategoryWithParts[] | undefined;
  setSelectedCategories: any;
  setSelectedByTableId: any;
  lead: ILead | null;
  vehicle: IVehicle | null;
}

export default function SelectedCategories({ selectedCategories, lead, vehicle, setSelectedCategories, setSelectedByTableId }: SelectedCategoriesProps) {
  const { create } = useJobMutations();
  const { createParts } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Job");
  const { saveHandlers: partsSaveHandlers } = useDefaultCRUDHandlers("Parts");
  const [totalTime, setTotalTime] = useState<number>(0);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [bodyTime, setBodyTime] = useState<number>(0);
  const [mechTime, setMechTime] = useState<number>(0);
  const [bodyPrice, setBodyPrice] = useState<number>(0);
  const [mechPrice, setMechPrice] = useState<number>(0);


  useEffect(() => {
    if (!selectedCategories || selectedCategories.length === 0) {
      return;
    }

    // Calculate total time and price
    setTotalTime(selectedCategories.map(cat => cat.category.whTime! + cat.category.disTime!).reduce((a, b) => a! + b!)!);
    setTotalPrice(selectedCategories.map(cat => cat.price!).reduce((a, b) => a! + b!)!);

    // Calculate body work time and price
    setBodyTime(selectedCategories.map(cat => !cat.isMech! ? cat.category.whTime! + cat.category.disTime! : 0).reduce((a, b) => a! + b!)!);
    setBodyPrice(selectedCategories.map(cat => !cat.isMech! ? cat.price! : 0).reduce((a, b) => a! + b!)!);

    // Calculate mechanical work time and price
    setMechTime(selectedCategories.map(cat => cat.isMech! ? cat.category.whTime! + cat.category.disTime! : 0).reduce((a, b) => a! + b!)!);
    setMechPrice(selectedCategories.map(cat => cat.isMech! ? cat.price! : 0).reduce((a, b) => a! + b!)!);

  }, [selectedCategories]);

  const handleCreateJob = () => {

    if (!selectedCategories || selectedCategories.length === 0) return;

    const newJob: CreateJobRequest = {
      type: JobType.Generic, //All jobs are generic now
      title: `${vehicle?.combinedMake} ${vehicle?.modelRangeDescription} - ${vehicle?.vrm}`,
      description: selectedCategories.map(cat => cat.category.name).join(", "),
      duration: totalTime,
      estimatedPrice: totalPrice, //If everything sells at the price entered
      estimatedMargin: null, //I don't think this is useful anymore as we don't have it for new parts
      leadId: lead?.id ?? null,
      vehicleId: vehicle?.id,
      jobPartCategories: selectedCategories.map(cat => ({
        partCategory: cat.category,
        averagePrice: cat.category.averagePrice,
        price: cat.price ?? 0,
        lastSoldPrice: cat.lastSoldPrice ?? cat.category.lastSoldPrice,
        note: cat.note
      })),
    }

    // console.log("new Job", newJob)

    create.mutate(newJob, {
      ...saveHandlers,
      onSuccess: () => {
        setSelectedCategories([]);
        setSelectedByTableId({});
        saveHandlers.onSuccess();
      }
    });
  }

  const handleCreateParts = (isDamaged: boolean) => {
    if (!selectedCategories || selectedCategories.length === 0) return;

    const newParts = selectedCategories.map(cat => ({
      categoryId: cat.category.id,
      price: cat.price ?? 0,
      averagePrice: cat.category.averagePrice,
      lastSoldPrice: cat.lastSoldPrice ?? cat.category.lastSoldPrice,
      note: cat.note,
      isDamaged
    }));

    const body = {
      vehicleId: vehicle?.id,
      leadId: lead?.id,
      parts: newParts
    }

    // console.log("new parts", newParts);

    createParts.mutate(body, {
      ...partsSaveHandlers,
      onSuccess: () => {
        setSelectedCategories([]);
        setSelectedByTableId({});
        saveHandlers.onSuccess();
      }
    });
  }

  if (!selectedCategories || selectedCategories.length === 0) {
    return null;
  };

  return (
    <>
      <div className="flex flex-col sticky pb-2 top-14 z-10 bg-gray-100">

        <div className="flex items-center gap-x-8 mt-4">

          <div className="bg-white shadow rounded-lg p-4 w-full">
            <h1 className="text-lg font-semibold">Categories Selected</h1>
            <p className="text-2xl pt-3 flex items-center gap-x-4">{selectedCategories.length}
              {selectedCategories.some(cat => !cat.price) && (
                <span className="text-gray-600 text-sm"> - {selectedCategories.filter(cat => !cat.price).length} categories have no price</span>
              )}
            </p>
          </div>

          <SummaryCard title="Total" time={totalTime} price={totalPrice} />
          <SummaryCard title="Body Work" time={bodyTime} price={bodyPrice} />
          <SummaryCard title="Mechanical Work" time={mechTime} price={mechPrice} />
        </div>

      </div>
      <div className="flex justify-between">
        <div className="flex gap-x-6">
          <ButtonInput label={`${lead?.jobs && lead.jobs.length > 0 ? "Update" : "Create"} Job`} onClick={() => handleCreateJob()} classes={undefined} isLoading={create.isLoading} />
          <ButtonInput label={"Make Available"} onClick={() => handleCreateParts(false)} classes={undefined} isLoading={createParts.isLoading} />
        </div>

        <div>
          <ButtonInput label={"Mark as Damaged"} onClick={() => handleCreateParts(true)} classes={"!bg-red-500 hover:!bg-red-600"} isLoading={createParts.isLoading} />
        </div>
      </div>
    </>
  )
}